import { Injectable } from '@angular/core';
import { DeploymentTypeMetadata } from '../models/deploymentForm.model';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FleetConfigurationService } from './fleet-configuration.service';

/**
 * Lightweight static cache for deployment/system form metadata.
 * Metadata is loaded once per application lifetime and never changes.
 * All consumers should access it synchronously via getters after the one-time set.
 * (Observable pattern removed to simplify usage & avoid unnecessary subscriptions.)
 */
@Injectable({ providedIn: 'root' })
export class SystemFormMetadataService {
  private metadataSnapshot: DeploymentTypeMetadata[] | null = null;

  constructor(private readonly fleetConfigurationService: FleetConfigurationService) { }

  /**
   * One-time setter for metadata. Subsequent calls with non-empty arrays are ignored
   * to enforce immutability for the application lifetime.
   */
  setMetadata(metadata: DeploymentTypeMetadata[]): void {
    if (!metadata || metadata.length === 0) { return; }
    if (this.metadataSnapshot && this.metadataSnapshot.length > 0) { return; } // already cached
    this.metadataSnapshot = metadata;
  }

  /** Returns cached metadata or null if not yet loaded. */
  getSnapshot(): DeploymentTypeMetadata[] | null {
    return this.metadataSnapshot;
  }

  /** Convenience accessor to retrieve all metadata (throws if not loaded yet). */
  getAll(): DeploymentTypeMetadata[] {
    if (!this.metadataSnapshot) {
      throw new Error('SystemFormMetadataService: metadata requested before it was loaded.');
    }
    return this.metadataSnapshot;
  }

  /** Find metadata entry by system name. */
  getByName(name: string): DeploymentTypeMetadata | undefined {
    return this.metadataSnapshot?.find(m => m.name === name);
  }

  /** Returns true when metadata has been loaded. */
  isLoaded(): boolean { return !!this.metadataSnapshot && this.metadataSnapshot.length > 0; }

  /**
   * Derived helper: returns normalized list of system types (uppercase name + numeric systemType).
   * Encapsulates repeated filter/map logic used by multiple components.
   */
  getSystemTypes(): { nameUpper: string; systemType: number }[] {
    return (this.metadataSnapshot || [])
      .filter(m => !!m?.name && m?.systemType !== undefined && m?.systemType !== null)
      .map(m => ({ nameUpper: m.name.toUpperCase(), systemType: m.systemType as number }));
  }

  /**
   * Ensures metadata is loaded; returns an observable of the metadata (cached or freshly fetched).
   * Components can subscribe and then call getSystemTypes().
   */
  loadMetadataIfNeeded(): Observable<DeploymentTypeMetadata[]> {
    if (this.isLoaded()) {
      return of(this.metadataSnapshot);
    }
    return this.fleetConfigurationService.getConfigTemplates().pipe(
      tap(metadata => this.setMetadata(metadata))
    );
  }
}
